export type Phase = 'larval' | 'juvenile' | 'pre-grow' | 'adult';
export type ClinicalStatus = 'pending' | 'confirmed' | 'rejected' | 'inconclusive';
export type ValidationResult = 'confirmed_anomaly' | 'rejected_alert' | 'inconclusive';
export type AnomalyType =
  | 'pseudoalbinism'
  | 'deformation'
  | 'lesion_spot'
  | 'mortality'
  | 'abnormal_behavior'
  | 'other'
  | 'not_applicable';
export type Severity = 'critical' | 'high' | 'medium' | 'low';

export interface ClientProfile {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  primary_color?: string;
  species_focus?: string;
  default_phase?: Phase;
}

export interface RawVisionAlert {
  id: string;
  case_id?: string;
  alert_id?: string;
  capture_id?: string;
  detection_id?: string;
  client_id?: string;
  project_id?: string;
  site_id?: string;
  tank_id?: string;
  kit_id?: string;
  userId?: string;
  userEmail?: string;
  imageUrl?: string;
  image_url?: string;
  species?: string;
  biological_phase?: string;
  detected_at?: string;
  detectedAt?: string;
  model_confidence?: number | string | null;
  modelConfidence?: number | string | null;
  alert_type?: string | null;
  alertType?: string | null;
  severity?: Severity | string | null;
  status?: 'por_tratar' | 'classificado' | 'resolvido' | 'detected' | 'under review' | 'validated' | 'discarded' | ClinicalStatus;
  problemName?: string;
  problemDescription?: string;
  proposedSolution?: string;
  inference?: string;
  metadata?: Record<string, any>;
  aiAnalysis?: {
    confidence?: number;
    detectedIssues?: string[];
    recommendation?: string;
  };
  createdAt: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  classifiedAt?: string;
  validationResult?: 'confirmed' | 'false_positive' | 'rejected' | ValidationResult;
  validation_result?: ValidationResult;
  anomalyType?: AnomalyType;
  anomaly_type?: AnomalyType;
  validationNotes?: string;
  validation_notes?: string;
  labObservations?: string;
  validated_at?: string;
  validatedAt?: string;
  validated_by?: string;
  validatedBy?: string;
  dashboardStatus?: Record<string, any>;
}

export interface ClinicalCase {
  id: string;
  clientId: string | null;
  projectId: string | null;
  clientName: string | null;
  species: string | null;
  phase: Phase | null;
  site: string | null;
  tank: string | null;
  image: string;
  alertType: string | null;
  suspectedPathology: string;
  confidence: number | null;
  severity: Severity;
  validationStatus: ClinicalStatus;
  validationResult: ValidationResult | null;
  anomalyType: AnomalyType | null;
  notes: string;
  rawAlert: RawVisionAlert;
  detectionCount: number;
  activeRiskScore: number;
  oxygen: number | null;
  temperature: number | null;
  ph: number | null;
  averageSizeGrams: number | null;
  biomassKg: number | null;
  growthTrendPct: number | null;
  capturedAt: string;
}

export interface GlobalScope {
  clientId?: string | null;
  projectId?: string | null;
  species?: string | null;
  phase?: Phase | null;
  site?: string | null;
  tank?: string | null;
}

const PHASES: Phase[] = ['larval', 'juvenile', 'pre-grow', 'adult'];

const titleCase = (value: string) =>
  value
    .replace(/_+/g, ' ')      // underscores → spaces (preserve hyphens for codes like T-04)
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word)
    .join(' ');

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizePhase = (value: unknown): Phase | null => {
  const normalized = String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');

  if (normalized.includes('larv')) return 'larval';
  if (normalized.includes('juven')) return 'juvenile';
  if (normalized.includes('pre') || normalized.includes('grow')) return 'pre-grow';
  if (normalized.includes('adult')) return 'adult';
  return null;
};

const normalizeTextValue = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_');

const SENSOR_ALERT_TYPES = new Set([
  'sensor',
  'temperature',
  'temperatura',
  'temp',
  'ph',
  'dissolved_oxygen',
  'oxygen',
  'oxigenio',
  'oxigenio_dissolvido',
  'o2',
  'redox',
  'orp',
  'salinity',
  'salinidade'
]);

const VISUAL_ALERT_TERMS = [
  'visual',
  'imagem',
  'image',
  'frame',
  'camera',
  'camara',
  'captura',
  'capture',
  'lesion',
  'lesao',
  'lesão',
  'spot',
  'mancha',
  'zona_avermelhada',
  'patologia',
  'pathology',
  'doenca',
  'doença',
  'anomaly',
  'anomalia',
  'yolo',
  'deteccao',
  'deteção',
  'detection'
];

const normalizeSensorType = (value: unknown): string | null => {
  const normalized = normalizeTextValue(value).replace(/^sensor_/, '');
  if (!normalized) return null;
  if (normalized === 'o2' || /(^|_)o2(_|$)/.test(normalized) || normalized.includes('oxygen') || normalized.includes('oxigenio')) return 'dissolved_oxygen';
  if (normalized.includes('temperatura') || normalized.includes('temperature') || normalized === 'temp') return 'temperature';
  if (normalized === 'ph' || normalized.includes('_ph') || normalized.includes('ph_')) return 'ph';
  if (normalized.includes('redox') || normalized.includes('orp')) return 'redox';
  if (normalized.includes('salin')) return 'salinity';
  return SENSOR_ALERT_TYPES.has(normalized) ? normalized : null;
};

export const getAlertSensorType = (alert?: RawVisionAlert | null): string | null => {
  if (!alert) return null;
  const metadata = alert.metadata ?? {};
  const explicit = [
    metadata.sensor_type,
    metadata.sensorType,
    metadata.metric,
    metadata.sensor_metric,
    metadata.parameter
  ].map(normalizeSensorType).find(Boolean);
  if (explicit) return explicit;

  const hasSensorSource = Boolean(
    metadata.sensor ||
    metadata.sensor_name ||
    metadata.sensor_id ||
    metadata.sensorId ||
    metadata.sensor_label
  );
  const typeText = [
    alert.alert_type,
    alert.alertType,
    alert.problemName,
    metadata.alert_type,
    metadata.alertType,
    metadata.rule_message,
    metadata.message
  ].map((value) => normalizeTextValue(value)).join(' ');

  return hasSensorSource || typeText.includes('sensor')
    ? normalizeSensorType(typeText) ?? 'sensor'
    : normalizeSensorType(typeText);
};

export const hasVisualEvidence = (alert?: RawVisionAlert | null) => {
  if (!alert) return false;
  const metadata = alert.metadata ?? {};
  const imageUrl = String(alert.imageUrl ?? alert.image_url ?? metadata.imageUrl ?? metadata.image_url ?? '').trim();
  if (imageUrl) return true;
  const visualText = [
    alert.alert_type,
    alert.alertType,
    alert.problemName,
    alert.inference,
    metadata.detection_status,
    metadata.capture_id,
    metadata.camera,
    metadata.camera_name,
    metadata.yolo_bbox,
    metadata.detection?.bbox
  ].map((value) => normalizeTextValue(value)).join(' ');
  return VISUAL_ALERT_TERMS.some((term) => visualText.includes(normalizeTextValue(term)));
};

export const isSensorDrivenAlert = (alert?: RawVisionAlert | null) =>
  Boolean(getAlertSensorType(alert)) && !hasVisualEvidence(alert);

const resolveClient = (alert: RawVisionAlert): ClientProfile | null => {
  const metadata = alert.metadata ?? {};
  const legacyPrefix = 'ten' + 'ant';
  const id = String(
    alert.client_id ??
    metadata.clientId ??
    metadata.client_id ??
    metadata[`${legacyPrefix}Id`] ??
    ''
  ).trim();
  const rawName = String(metadata.client ?? metadata.clientName ?? metadata[legacyPrefix] ?? '').trim();
  const rawSlug = String(metadata.clientSlug ?? metadata.slug ?? metadata[`${legacyPrefix}Slug`] ?? '').trim();

  if (!id && !rawName && !rawSlug) return null;

  const name = rawName || rawSlug || id;
  return {
    id: id || `client-${slugify(name)}`,
    name: titleCase(name),
    slug: slugify(rawSlug || name),
    logo_url: metadata.logo_url ? String(metadata.logo_url) : undefined,
    primary_color: metadata.primaryColor ? String(metadata.primaryColor) : undefined,
    species_focus: metadata.species_focus ? String(metadata.species_focus) : undefined,
    default_phase: normalizePhase(metadata.default_phase)
  };
};

const resolveSpecies = (alert: RawVisionAlert, client: ClientProfile | null) => {
  const value = String(
    alert.species ??
    alert.metadata?.species ??
    alert.metadata?.fishSpecies ??
    alert.metadata?.species_focus ??
    client?.species_focus ??
    ''
  ).trim();
  return value ? titleCase(value) : null;
};

const resolveSite = (alert: RawVisionAlert) => {
  const value = String(alert.metadata?.site ?? alert.metadata?.locationGroup ?? alert.metadata?.farm ?? '').trim();
  return value ? titleCase(value) : null;
};

const resolveTank = (alert: RawVisionAlert) => {
  const value = String(
    alert.metadata?.tank ??
    alert.metadata?.tankName ??
    alert.metadata?.location ??
    alert.metadata?.camera ??
    ''
  );
  return value.trim() ? titleCase(value) : null;
};

const normalizeValidationResult = (value: unknown): ValidationResult | null => {
  const normalized = normalizeTextValue(value);
  if (normalized === 'confirmed_anomaly' || normalized === 'confirmed') return 'confirmed_anomaly';
  if (normalized === 'rejected_alert' || normalized === 'rejected' || normalized === 'false_positive') return 'rejected_alert';
  if (normalized === 'inconclusive') return 'inconclusive';
  return null;
};

const resolveStatus = (alert: RawVisionAlert): ClinicalStatus => {
  const explicitStatus = normalizeTextValue(alert.status ?? alert.metadata?.status);
  if (explicitStatus === 'pending') return 'pending';
  if (explicitStatus === 'confirmed' || explicitStatus === 'validated') return 'confirmed';
  if (explicitStatus === 'rejected' || explicitStatus === 'discarded') return 'rejected';
  if (explicitStatus === 'inconclusive') return 'inconclusive';

  const validationResult = normalizeValidationResult(alert.validation_result ?? alert.validationResult);
  if (validationResult === 'confirmed_anomaly') return 'confirmed';
  if (validationResult === 'rejected_alert') return 'rejected';
  if (validationResult === 'inconclusive') return 'inconclusive';

  if (explicitStatus === 'resolvido') return 'confirmed';
  return 'pending';
};

const resolveSeverity = (alert: RawVisionAlert, confidence: number | null): Severity => {
  const explicitSeverity = normalizeTextValue(alert.severity ?? alert.metadata?.severity);
  if (explicitSeverity === 'critical') return 'critical';
  if (explicitSeverity === 'high') return 'high';
  if (explicitSeverity === 'medium') return 'medium';
  if (explicitSeverity === 'low') return 'low';

  const detectedIssues = alert.aiAnalysis?.detectedIssues?.length ?? 0;
  const text = `${alert.problemName ?? ''} ${alert.problemDescription ?? ''} ${alert.inference ?? ''}`.toLowerCase();
  const critical = ['ulcer', 'lesao', 'parasite', 'parasita', 'hemorrhage', 'grave', 'stress', 'risk'].some((term) =>
    text.includes(term)
  );

  if (critical) return 'critical';
  if (detectedIssues >= 2 || (confidence !== null && confidence < 72)) return 'high';
  if (detectedIssues >= 1 || (confidence !== null && confidence < 88)) return 'medium';
  return 'low';
};

const numberFrom = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const confidenceFrom = (...values: unknown[]): number | null => {
  const value = numberFrom(values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== ''));
  if (value === null) return null;
  return value <= 1 ? Math.round(value * 100) : Math.round(value);
};

const resolveAlertType = (alert: RawVisionAlert) => {
  const value = String(
    alert.alert_type ??
    alert.alertType ??
    alert.metadata?.alert_type ??
    alert.metadata?.alertType ??
    alert.problemName ??
    alert.aiAnalysis?.detectedIssues?.[0] ??
    alert.inference ??
    ''
  ).trim();

  return value ? titleCase(value) : null;
};

const normalizeAnomalyType = (value: unknown): AnomalyType | null => {
  const normalized = normalizeTextValue(value);
  if (normalized === 'pseudoalbinism' || normalized === 'pseudoalbinismo') return 'pseudoalbinism';
  if (normalized === 'deformation' || normalized === 'deformacao' || normalized === 'deformação') return 'deformation';
  if (normalized === 'lesion_spot' || normalized === 'lesao_mancha' || normalized === 'lesão_mancha') return 'lesion_spot';
  if (normalized === 'mortality' || normalized === 'mortalidade') return 'mortality';
  if (normalized === 'abnormal_behavior' || normalized === 'comportamento_anomalo' || normalized === 'comportamento_anómalo') return 'abnormal_behavior';
  if (normalized === 'other' || normalized === 'outro') return 'other';
  if (normalized === 'not_applicable' || normalized === 'nao_aplicavel' || normalized === 'não_aplicável') return 'not_applicable';
  return null;
};

export const toClinicalCase = (alert: RawVisionAlert): ClinicalCase => {
  const client = resolveClient(alert);
  const species = resolveSpecies(alert, client);
  const phase = normalizePhase(alert.biological_phase ?? alert.metadata?.biological_phase ?? alert.metadata?.phase ?? client?.default_phase);
  const site = resolveSite(alert);
  const tank = resolveTank(alert);
  const confidencePct = confidenceFrom(alert.model_confidence, alert.modelConfidence, alert.metadata?.model_confidence, alert.metadata?.confidence, alert.aiAnalysis?.confidence);
  const severity = resolveSeverity(alert, confidencePct);
  const status = resolveStatus(alert);
  const validationResult = normalizeValidationResult(alert.validation_result ?? alert.validationResult);
  const anomalyType = normalizeAnomalyType(alert.anomaly_type ?? alert.anomalyType ?? alert.metadata?.anomaly_type);
  const oxygen = numberFrom(alert.metadata?.oxygen ?? alert.metadata?.o2);
  const temperature = numberFrom(alert.metadata?.temperature ?? alert.metadata?.temp);
  const ph = numberFrom(alert.metadata?.ph);
  const averageSizeGrams = numberFrom(alert.metadata?.averageSizeGrams ?? alert.metadata?.estimatedSize);
  const biomassKg = numberFrom(alert.metadata?.biomassKg ?? alert.metadata?.estimatedBiomass);
  const growthTrendPct = numberFrom(alert.metadata?.growthTrendPct ?? alert.metadata?.growthDelta);
  const alertType = resolveAlertType(alert);
  const capturedAt = String(alert.detected_at ?? alert.detectedAt ?? alert.metadata?.Timestamp ?? alert.created_at ?? alert.createdAt);

  return {
    id: alert.case_id ?? alert.id ?? alert.alert_id,
    clientId: client?.id ?? null,
    projectId: String(alert.project_id ?? alert.metadata?.project_id ?? alert.metadata?.projectId ?? '').trim() || null,
    clientName: client?.name ?? null,
    species,
    phase,
    site,
    tank,
    image: alert.imageUrl ?? alert.image_url ?? '',
    alertType,
    suspectedPathology: alertType ?? 'A confirmar',
    confidence: confidencePct,
    severity,
    validationStatus: status,
    validationResult,
    anomalyType,
    notes: alert.validation_notes || alert.validationNotes || alert.labObservations || alert.problemDescription || alert.aiAnalysis?.recommendation || 'Ainda não existem dados suficientes.',
    rawAlert: alert,
    detectionCount: numberFrom(alert.metadata?.detectionCount ?? alert.metadata?.detections) ?? 1,
    activeRiskScore: severity === 'critical' ? 96 : severity === 'high' ? 85 : severity === 'medium' ? 60 : 30,
    oxygen,
    temperature,
    ph,
    averageSizeGrams,
    biomassKg,
    growthTrendPct,
    capturedAt
  };
};

export const normalizeAlertsToCases = (alerts: RawVisionAlert[]) => alerts.map(toClinicalCase);

export const buildClientCatalog = (alerts: RawVisionAlert[]) => {
  const map = new Map<string, ClientProfile>();
  alerts.forEach((alert) => {
    const client = resolveClient(alert);
    if (!client) return;
    map.set(client.id, client);
  });
  return Array.from(map.values());
};

export const applyGlobalScope = (cases: ClinicalCase[], scope: GlobalScope) =>
  cases.filter((item) => {
    if (scope.clientId && item.clientId !== scope.clientId) return false;
    if (scope.projectId && item.projectId !== scope.projectId) return false;
    if (scope.species && item.species !== scope.species) return false;
    if (scope.phase && item.phase !== scope.phase) return false;
    if (scope.site && item.site !== scope.site) return false;
    if (scope.tank && item.tank !== scope.tank) return false;
    return true;
  });

export const collectScopeOptions = (cases: ClinicalCase[]) => {
  const species = Array.from(new Set(cases.map((item) => item.species).filter((item): item is string => Boolean(item)))).sort();
  const phases = PHASES.filter((phase) => cases.some((item) => item.phase === phase));
  const sites = Array.from(new Set(cases.map((item) => item.site).filter((item): item is string => Boolean(item)))).sort();
  const tanks = Array.from(new Set(cases.map((item) => item.tank).filter((item): item is string => Boolean(item)))).sort();
  return { species, phases, sites, tanks };
};

export const computeValidationRate = (cases: ClinicalCase[]) => {
  if (!cases.length) return 0;
  const reviewed = cases.filter((item) => item.validationStatus !== 'pending').length;
  return Math.round((reviewed / cases.length) * 100);
};

export const riskLabel = (score: number): 'high' | 'medium' | 'low' => {
  if (score >= 72) return 'high';
  if (score >= 46) return 'medium';
  return 'low';
};

export const summarizeInsights = (cases: ClinicalCase[]) => {
  if (!cases.length) {
    return 'No clinical cases match the current client scope. Expand the filters to recover operational context.';
  }

  const highRisk = cases.filter((item) => item.severity === 'high');
  const lowOxygen = cases.filter((item) => item.oxygen !== null && item.oxygen < 5.5);
  const phaseCounts = new Map<string, number>();
  cases.forEach((item) => {
    if (item.phase) phaseCounts.set(item.phase, (phaseCounts.get(item.phase) ?? 0) + 1);
  });
  const dominantPhase = Array.from(phaseCounts.entries()).sort((a, b) => b[1] - a[1])[0];
  const topTank = Array.from(
    cases.reduce((acc, item) => {
      if (!item.tank) return acc;
      acc.set(item.tank, Math.max(acc.get(item.tank) ?? 0, item.activeRiskScore));
      return acc;
    }, new Map<string, number>())
  ).sort((a, b) => b[1] - a[1])[0];

  if (highRisk.length && lowOxygen.length) {
    return `${topTank?.[0] ?? 'One tank'} shows the highest clinical pressure, and low oxygen is co-occurring with elevated review demand. Prioritize site verification and validation workflow before the next capture cycle.`;
  }

  if (dominantPhase) {
    return `Most detections are concentrated in the ${dominantPhase[0]} phase. Focus review effort there first and compare growth drift against tank-level biomass variation.`;
  }

  return 'The current client scope is stable overall. Keep validation cadence tight and watch for combined sensor drift before raising escalation.';
};

export const groupByTank = (cases: ClinicalCase[]) =>
  Array.from(
    cases.reduce((acc, item) => {
      if (!item.tank) return acc;
      const current = acc.get(item.tank) ?? {
        tank: item.tank,
        site: item.site,
        cases: 0,
        highRiskCases: 0,
        avgRisk: 0,
        avgSizeGrams: 0,
        biomassKg: 0,
        oxygen: [] as number[],
        timestamps: [] as string[]
      };
      current.cases += 1;
      current.highRiskCases += item.severity === 'high' ? 1 : 0;
      current.avgRisk += item.activeRiskScore;
      if (item.averageSizeGrams !== null) current.avgSizeGrams += item.averageSizeGrams;
      if (item.biomassKg !== null) current.biomassKg += item.biomassKg;
      if (item.oxygen !== null) current.oxygen.push(item.oxygen);
      current.timestamps.push(item.capturedAt);
      acc.set(item.tank, current);
      return acc;
    }, new Map<string, {
      tank: string;
      site: string | null;
      cases: number;
      highRiskCases: number;
      avgRisk: number;
      avgSizeGrams: number;
      biomassKg: number;
      oxygen: number[];
      timestamps: string[];
    }>())
  )
    .map(([_, item]) => ({
      ...item,
      avgRisk: Math.round(item.avgRisk / item.cases),
      avgSizeGrams: Math.round(item.avgSizeGrams / item.cases),
      biomassKg: Math.round(item.biomassKg),
      oxygenMean: item.oxygen.length ? Number((item.oxygen.reduce((sum, value) => sum + value, 0) / item.oxygen.length).toFixed(1)) : null,
      lastCapturedAt: item.timestamps.sort().slice(-1)[0]
    }))
    .sort((a, b) => b.avgRisk - a.avgRisk);
